// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';

// ** Components
import ArrowSlideHoverCta from '@/components/common/ArrowSlideHoverCta';
import PageContentContainer from '@/components/common/PageContentContainer';
import SwiperCarousel, {
	type SwiperCarouselSlide,
	type SwiperCarouselControls
} from '@/components/common/carousel/SwiperCarousel';
import { Icon } from '@/components/common/icons';

type BreakpointCardsPerView = Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number>>;

export type CardCarouselCardsPerView = number | BreakpointCardsPerView;

const toSxArray = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : sx ? [sx] : []);

export type CardCarouselProps<TItem> = {
	items: TItem[];
	getItemKey: (item: TItem, index: number) => React.Key;
	renderItem: (item: TItem, index: number) => React.ReactNode;
	cardsPerView: CardCarouselCardsPerView;
	title?: string;
	showTitle?: boolean;
	ctaLabel?: string;
	ctaHref?: string;
	showCta?: boolean;
	indicatorCount?: number;
	spaceBetween?: number;
	showArrowControls?: boolean;
	prevAriaLabel?: string;
	nextAriaLabel?: string;
	sx?: SxProps<Theme>;
	viewportSx?: SxProps<Theme>;
	slideSx?: SxProps<Theme>;
};

const CardCarousel = <TItem,>({
	items,
	getItemKey,
	renderItem,
	cardsPerView = 1,
	title,
	showTitle = false,
	ctaLabel = 'Alle Eintraege',
	ctaHref = '#',
	showCta = false,
	indicatorCount = 3,
	spaceBetween = 4,
	showArrowControls = true,
	prevAriaLabel = 'Vorherige Eintraege',
	nextAriaLabel = 'Naechste Eintraege',
	sx,
	viewportSx,
	slideSx
}: CardCarouselProps<TItem>) => {
	const theme = useTheme();
	const sp = theme.spacing;
	const toCardWidth = React.useCallback(
		(perView: number) => `calc((100vw - ((${perView} - 1) * ${sp(spaceBetween)})) / ${perView})`,
		[spaceBetween, sp]
	);

	const cardsPerViewItemSx = React.useMemo<SxProps<Theme> | undefined>(() => {
		if (typeof cardsPerView === 'number') {
			const width = toCardWidth(cardsPerView);
			return { width, minWidth: width, maxWidth: width };
		}

		const widthsByBreakpoint = Object.entries(cardsPerView).reduce<Record<string, string>>(
			(acc, [breakpoint, perView]) => {
				if (!perView) return acc;
				acc[breakpoint] = toCardWidth(perView);
				return acc;
			},
			{}
		);

		return {
			width: widthsByBreakpoint,
			minWidth: widthsByBreakpoint,
			maxWidth: widthsByBreakpoint
		};
	}, [cardsPerView, toCardWidth]);

	const slides = React.useMemo<SwiperCarouselSlide[]>(
		() =>
			items.map((item, index) => {
				return {
					key: getItemKey(item, index),
					sx: toSxArray(cardsPerViewItemSx),
					content: renderItem(item, index)
				};
			}),
		[cardsPerViewItemSx, getItemKey, items, renderItem]
	);
	const indicatorIndexes = Array.from({ length: indicatorCount }, (_, index) => index);

	const renderHeader = () => {
		if (!showTitle && !showCta) return null;

		return (
			<PageContentContainer>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexWrap: 'wrap',
						gap: 2
					}}
				>
					{showTitle && title && (
						<Typography
							variant="title-lg"
							component="h2"
							sx={{
								color: 'common.black',
								fontSize: { xs: '2rem', md: '2.5rem' },
								lineHeight: { xs: '2.5rem', md: '3rem' },
								letterSpacing: { xs: '-0.64px', md: '-1.2px' }
							}}
						>
							{title}
						</Typography>
					)}

					{showCta && (
						<ArrowSlideHoverCta
							href={ctaHref}
							label={ctaLabel}
							color={theme.palette.common.black}
							typographyVariant="body-lg"
							fontWeight={500}
						/>
					)}
				</Box>
			</PageContentContainer>
		);
	};

	const renderFooter = ({ activeIndicator, canGoPrev, canGoNext, goPrev, goNext }: SwiperCarouselControls) => (
		<PageContentContainer maxWidthPx={theme.layout.contentMaxWidthPx}>
			<Box
				sx={{
					position: 'relative',
					mt: { xs: sp(12), md: sp(16) },
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
					<Box component="span" sx={visuallyHidden} aria-live="polite">
						Slide {activeIndicator + 1} von {indicatorIndexes.length}
					</Box>
					{indicatorIndexes.map((indicatorIndex) => (
						<Box
							key={`indicator-${indicatorIndex + 1}`}
							aria-hidden="true"
							sx={{
								width: 8,
								height: 8,
								borderRadius: '2px',
								backgroundColor:
									activeIndicator === indicatorIndex ? theme.palette.common.black : theme.palette.grey[500],
								transition: 'background-color 160ms ease'
							}}
						/>
					))}
				</Box>

				{showArrowControls && (
					<Box
						sx={{
							position: 'absolute',
							right: 0,
							top: '50%',
							transform: 'translateY(-50%)',
							display: { xs: 'none', md: 'flex' },
							alignItems: 'center',
							columnGap: 4,
							color: theme.palette.common.black
						}}
					>
						<IconButton
							size="small"
							onClick={goPrev}
							disabled={!canGoPrev}
							aria-label={prevAriaLabel}
							disableRipple
							sx={{
								p: 0.5,
								borderRadius: 999,
								color: theme.palette.common.black,
								backgroundColor: 'transparent !important',
								'&:hover': {
									color: theme.palette.primary.main,
									backgroundColor: 'transparent !important'
								},
								'& .MuiTouchRipple-root': { display: 'none' }
							}}
						>
							<Icon name="arrowLeft" opacity={canGoPrev ? 1 : 0.32} />
						</IconButton>
						<IconButton
							size="small"
							onClick={goNext}
							disabled={!canGoNext}
							aria-label={nextAriaLabel}
							disableRipple
							sx={{
								p: 0.5,
								borderRadius: 999,
								color: theme.palette.common.black,
								backgroundColor: 'transparent !important',
								'&:hover': {
									color: theme.palette.primary.main,
									backgroundColor: 'transparent !important'
								},
								'& .MuiTouchRipple-root': { display: 'none' }
							}}
						>
							<Icon name="arrowRight" opacity={canGoNext ? 1 : 0.32} />
						</IconButton>
					</Box>
				)}
			</Box>
		</PageContentContainer>
	);

	return (
		<React.Fragment>
			{renderHeader()}

			<SwiperCarousel
				slides={slides}
				spaceBetween={spaceBetween}
				indicatorCount={indicatorCount}
				sx={sx}
				viewportSx={[{ mt: { xs: 8, md: 10 } }, ...toSxArray(viewportSx)]}
				slideSx={[
					{
						boxSizing: 'border-box',
						paddingTop: sp(1),
						paddingBottom: sp(2)
					},
					...toSxArray(slideSx)
				]}
				renderFooter={renderFooter}
			/>
		</React.Fragment>
	);
};

export default CardCarousel;
